import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, Copy, ExternalLink, RefreshCw } from 'lucide-react';
import SlideOutDrawer from '../../../components/SlideOutDrawer';
import CodeSnippet from '../../../components/CodeSnippet';
import { runWorkflowEvalSuggestions } from '../../../services/workflowEvalSuggestionsApi';
import { useSessionJwt } from '../../../hooks/useSessionJwt';
import { components, palette, withOpacity } from '../../../utils/designTokens';
import { shortId } from '../rca/rcaHelpers';

function formatLabel(value) {
  return String(value || '')
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

const EVAL_TYPE_LABELS = {
  'rag_quality.faithfulness': 'Answer quality',
  'tool_correctness.required_tool_called': 'Tool use',
  'golden_path.workflow_regression': 'Happy path',
  'resiliency.error_handling': 'Error handling',
  'output_contract.schema_compliance': 'Output format',
};

const ASSERTION_TYPE_LABELS = {
  workflow_reaches_successful_outcome: 'Business task reaches successful outcome',
  workflow_handles_observed_error_safely: 'Business task handles observed error safely',
};

const LIMITATION_MESSAGES = {
  tool_schema_not_observed: "Lumoz saw this tool being called but not its parameters. This eval can check that it's called, not how.",
  some_seed_tool_calls_failed: 'Some test runs include failed calls to this tool.',
  recovery_policy_not_observed: 'No run shows how the app should recover, so the expected behavior must be written by hand.',
  schema_inferred_from_observed_outputs: 'The output format is inferred from what the app returned, not from a declared schema.',
  retrieved_documents_truncated: 'Some retrieved documents were truncated before Lumoz saw them.',
  retrieved_document_text_unavailable: 'Retrieved document text was not available in the trace data.',
  some_traces_have_metadata_only: 'Some traces only include metadata, so the eval may need fixtures filled in by hand.',
};

const CATEGORY_ORDER = ['Tool use', 'Answer quality', 'Happy path', 'Error handling', 'Output format', 'Other'];

function formatEvalType(value) {
  return EVAL_TYPE_LABELS[value] || 'Other';
}

function formatAssertionType(value, fallback) {
  return ASSERTION_TYPE_LABELS[value] || fallback || formatLabel(value);
}

function limitationText(value) {
  return LIMITATION_MESSAGES[value] || `Needs review: ${formatLabel(value)}.`;
}

function readinessLabel(value) {
  return value === 'high' ? 'Ready to build' : 'Needs input';
}

function readinessRank(value) {
  return value === 'high' ? 0 : 1;
}

function frameworkLabel(value) {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'deepeval') return 'DeepEval';
  if (normalized === 'pytest') return 'pytest';
  return value || '';
}

function suggestionKey(suggestion) {
  return suggestion?.eval_suggestion_id || `${suggestion?.eval_type || 'eval'}:${suggestion?.source_pattern_key || ''}`;
}

function categoryRank(category) {
  const idx = CATEGORY_ORDER.indexOf(category);
  return idx >= 0 ? idx : CATEGORY_ORDER.length;
}

function stringifyJson(value) {
  if (!value || (typeof value === 'object' && Object.keys(value).length === 0)) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function workflowName(workflow) {
  return workflow?.workflowName || workflow?.canonical_name || 'Business task';
}

function workflowVersion(workflow) {
  return Number(workflow?.workflowVersion ?? workflow?.workflow_version ?? 1) || 1;
}

function traceSetRows(traceSet) {
  if (!traceSet || typeof traceSet !== 'object') return [];
  return Object.entries(traceSet)
    .filter(([, rows]) => Array.isArray(rows) && rows.length > 0)
    .map(([key, rows]) => ({
      key,
      label: key === 'primary_failure' ? 'Test cases from runs' : formatLabel(key),
      rows,
    }));
}

function flattenedTraceRows(traceSet) {
  const seen = new Set();
  const rows = [];
  traceSetRows(traceSet).forEach(group => {
    group.rows.forEach(item => {
      const traceId = item?.trace_id || '';
      if (!traceId || seen.has(traceId)) return;
      seen.add(traceId);
      rows.push(item);
    });
  });
  return rows;
}

function traceSetSignature(traceSet) {
  return flattenedTraceRows(traceSet).map(item => item?.trace_id || '').sort().join('|');
}

function traceUserInput(item) {
  return item?.summary?.user_input
    || item?.summary?.query
    || item?.summary?.input
    || item?.summary?.request
    || shortId(item?.trace_id);
}

function traceHref({ appId, environment, selectedRange, traceId }) {
  if (!appId || !traceId) return '';
  const params = new URLSearchParams();
  if (environment) params.set('env', environment);
  if (selectedRange) params.set('range', selectedRange);
  const query = params.toString();
  return `/app/${encodeURIComponent(appId)}/traces/${encodeURIComponent(traceId)}${query ? `?${query}` : ''}`;
}

function ReadinessBadge({ readiness }) {
  const value = readiness || 'partial';
  const color = value === 'high' ? palette.status.success : palette.status.warning;
  return (
    <span
      className="rounded border px-2 py-1 text-[11px] font-semibold"
      style={{
        color,
        borderColor: withOpacity(color, 35),
        backgroundColor: withOpacity(color, 10),
      }}
    >
      {readinessLabel(value)}
    </span>
  );
}

function CopyTextButton({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(async (event) => {
    event?.stopPropagation?.();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.error('Failed to copy eval suggestion prompt', err);
    }
  }, [text]);

  return (
    <button
      type="button"
      className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs ${components.button.secondary}`}
      onClick={copy}
      disabled={!text}
      title={copied ? 'Copied' : label}
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
      {copied ? 'Copied' : label}
    </button>
  );
}

function buildAgentPrompt(suggestion, context) {
  const evalSpecJson = stringifyJson(suggestion?.eval_spec);
  const traceIds = flattenedTraceRows(suggestion?.trace_set).map(item => item?.trace_id).filter(Boolean);
  const parts = [
    'Implement this Lumoz business task eval.',
    '',
    `App ID: ${context.appId || ''}`,
    `Environment: ${context.environment || ''}`,
    `Business task ID: ${context.workflowId || ''}`,
    `Business task version: ${context.version || ''}`,
    `Business task name: ${context.name || ''}`,
    ...(traceIds.length ? [`Trace IDs: ${traceIds.join(', ')}`] : []),
    '',
    'Implementation prompt:',
    suggestion?.implementation_prompt || '',
  ];
  if (evalSpecJson) {
    parts.push('', 'Eval spec JSON:', evalSpecJson);
  }
  return parts.join('\n');
}

function TraceCasesSection({ rows, appId, environment, selectedRange, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!rows.length) return null;

  const reasons = rows.map(item => item?.reason || '').filter(Boolean);
  const repeatedReason = reasons.length > 1 && new Set(reasons).size === 1 ? reasons[0] : '';

  return (
    <div className="rounded border border-border-subtle bg-background-secondary">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
        onClick={() => setOpen(value => !value)}
      >
        <span className="text-xs font-semibold text-text-primary">Test cases from runs ({rows.length})</span>
        <ChevronDown size={14} className={`text-text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="space-y-1 border-t border-border-subtle px-3 py-2">
          {rows.slice(0, 8).map((item, idx) => {
            const traceId = item?.trace_id || '';
            const href = traceHref({ appId, environment, selectedRange, traceId });
            return (
              <div key={`${traceId || idx}`} className="flex min-w-0 items-center justify-between gap-3 text-xs">
                <div className="min-w-0 truncate text-text-primary" title={traceUserInput(item)}>
                  {traceUserInput(item)}
                  {item?.reason && item.reason !== repeatedReason ? (
                    <span className="text-text-muted"> · {item.reason}</span>
                  ) : null}
                </div>
                {href ? (
                  <a
                    href={href}
                    className="inline-flex shrink-0 items-center gap-1 font-mono text-accent-primary-base hover:underline"
                    onClick={event => event.stopPropagation()}
                  >
                    {shortId(traceId)}
                    <ExternalLink size={11} />
                  </a>
                ) : (
                  <span className="shrink-0 font-mono text-accent-primary-base">{shortId(traceId)}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SuggestionCard({
  suggestion,
  expanded,
  onToggle,
  appId,
  environment,
  selectedRange,
  context,
  hideTraceCases = false,
}) {
  const assertions = Array.isArray(suggestion?.eval_spec?.assertions) ? suggestion.eval_spec.assertions : [];
  const frameworks = Array.isArray(suggestion?.target_frameworks) ? suggestion.target_frameworks : [];
  const limitations = Array.isArray(suggestion?.limitations) ? suggestion.limitations : [];
  const traceRows = flattenedTraceRows(suggestion?.trace_set);
  const evalSpecJson = stringifyJson(suggestion?.eval_spec);
  const category = formatEvalType(suggestion?.eval_type);
  const copyPrompt = buildAgentPrompt(suggestion, context);

  return (
    <div className="rounded border border-border-subtle bg-background-primary">
      <button
        type="button"
        className="w-full px-3 py-3 text-left"
        onClick={onToggle}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-text-primary">
              {suggestion?.title || 'Eval suggestion'}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-text-muted">
              <span>{category}</span>
              {frameworks.map(framework => (
                <span key={framework} className="rounded border border-border-subtle px-1.5 py-0.5">
                  {frameworkLabel(framework)}
                </span>
              ))}
            </div>
            {limitations.length > 0 && (
              <div className="mt-2 space-y-1">
                {limitations.map(limitation => (
                  <div key={limitation} className="text-xs leading-relaxed" style={{ color: palette.status.warning }}>
                    {limitationText(limitation)}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <ReadinessBadge readiness={suggestion?.readiness} />
            <ChevronDown size={15} className={`text-text-muted transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border-subtle px-3 pb-3 pt-3">
          {suggestion?.summary && (
            <p className="text-sm leading-relaxed text-text-primary">{suggestion.summary}</p>
          )}

          {assertions.length > 0 && (
            <div className="mt-3 space-y-2">
              {assertions.map((assertion, idx) => (
                <div
                  key={`${assertion?.type || 'assertion'}:${idx}`}
                  className="flex items-start gap-2 text-xs leading-relaxed text-text-primary"
                  title={assertion?.type || ''}
                >
                  <span className="mt-0.5 text-accent-primary-base">✓</span>
                  <span>{assertion?.description || formatAssertionType(assertion?.type, `Assertion ${idx + 1}`)}</span>
                </div>
              ))}
            </div>
          )}

          {!hideTraceCases && (
            <div className="mt-3">
              <TraceCasesSection
                rows={traceRows}
                appId={appId}
                environment={environment}
                selectedRange={selectedRange}
              />
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <CopyTextButton text={copyPrompt} label="Copy prompt for your coding agent" />
            {evalSpecJson && (
              <details className="min-w-0 rounded border border-border-subtle bg-background-secondary">
                <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-text-primary">
                  Eval spec JSON
                </summary>
                <div className="border-t border-border-subtle p-3">
                  <CodeSnippet code={evalSpecJson} language="json" />
                </div>
              </details>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function groupSuggestions(suggestions) {
  const sorted = [...suggestions].sort((a, b) => (
    readinessRank(a?.readiness) - readinessRank(b?.readiness)
    || categoryRank(formatEvalType(a?.eval_type)) - categoryRank(formatEvalType(b?.eval_type))
    || String(a?.title || '').localeCompare(String(b?.title || ''))
  ));
  return sorted.reduce((groups, suggestion) => {
    const category = formatEvalType(suggestion?.eval_type);
    const key = CATEGORY_ORDER.includes(category) ? category : 'Other';
    if (!groups[key]) groups[key] = [];
    groups[key].push(suggestion);
    return groups;
  }, {});
}

function allCopyPrompt(suggestions, context) {
  return suggestions.map((suggestion, idx) => [
    `Eval ${idx + 1}: ${suggestion?.title || 'Eval suggestion'}`,
    buildAgentPrompt(suggestion, context),
  ].join('\n')).join('\n\n---\n\n');
}

function sharedTraceRows(suggestions) {
  if (!suggestions.length) return [];
  const signatures = suggestions.map(suggestion => traceSetSignature(suggestion?.trace_set));
  if (!signatures[0] || signatures.some(signature => signature !== signatures[0])) return [];
  return flattenedTraceRows(suggestions[0]?.trace_set);
}

export default function WorkflowEvalSuggestionsDrawer({
  isOpen,
  onClose,
  appId,
  environment,
  selectedRange,
  workflow,
  isDemo = false,
}) {
  const { getJwt } = useSessionJwt();
  const [suggestions, setSuggestions] = useState([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});

  const name = workflowName(workflow);
  const version = workflowVersion(workflow);
  const workflowId = workflow?.workflowId || workflow?.workflow_id || '';
  const drawerContext = useMemo(() => ({
    appId,
    environment,
    workflowId,
    version,
    name,
  }), [appId, environment, name, version, workflowId]);

  const loadSuggestions = useCallback(async ({ force = false, signal } = {}) => {
    if (isDemo) {
      setSuggestions([]);
      setStatus('unsupported');
      setError('');
      return;
    }
    const jwt = getJwt();
    if (!appId || !environment || !workflowId || !jwt) {
      setSuggestions([]);
      setStatus('');
      setError('');
      return;
    }

    if (force) {
      setRegenerating(true);
    } else {
      setLoading(true);
    }
    setError('');
    try {
      const response = await runWorkflowEvalSuggestions(appId, environment, selectedRange, workflowId, jwt, {
        workflowVersion: version,
        force,
        signal,
      });
      setSuggestions(Array.isArray(response?.suggestions) ? response.suggestions : []);
      setStatus(response?.status || '');
      setExpandedCards({});
    } catch (err) {
      if (err?.name !== 'AbortError') {
        setSuggestions([]);
        setStatus('');
        setError(err?.message || 'Failed to load eval suggestions');
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
        setRegenerating(false);
      }
    }
  }, [appId, environment, getJwt, isDemo, selectedRange, version, workflowId]);

  useEffect(() => {
    if (!isOpen) {
      setLoading(false);
      setRegenerating(false);
      return undefined;
    }
    const abortController = new AbortController();
    setSuggestions([]);
    setStatus('');
    setError('');
    loadSuggestions({ signal: abortController.signal });
    return () => abortController.abort();
  }, [isOpen, loadSuggestions]);

  const statusText = useMemo(() => {
    if (!status) return '';
    if (status === 'generated') return 'Generated';
    if (status === 'existing') return 'Existing';
    if (status === 'unsupported') return 'Unsupported';
    return formatLabel(status);
  }, [status]);

  const regenerate = useCallback(() => {
    loadSuggestions({ force: true });
  }, [loadSuggestions]);

  const groupedSuggestions = useMemo(() => groupSuggestions(suggestions), [suggestions]);
  const sharedRows = useMemo(() => sharedTraceRows(suggestions), [suggestions]);
  const readyCount = useMemo(
    () => suggestions.filter(suggestion => suggestion?.readiness === 'high').length,
    [suggestions],
  );
  const traceCount = sharedRows.length || new Set(
    suggestions.flatMap(suggestion => flattenedTraceRows(suggestion?.trace_set).map(item => item?.trace_id).filter(Boolean)),
  ).size;
  const headerSummary = suggestions.length > 0
    ? `${suggestions.length} suggested evals · ${readyCount} ready to build · built from ${traceCount} runs`
    : '';
  const copyAllText = useMemo(
    () => allCopyPrompt(suggestions, drawerContext),
    [drawerContext, suggestions],
  );

  const toggleCard = useCallback((key) => {
    setExpandedCards(previous => ({ ...previous, [key]: !previous[key] }));
  }, []);

  return (
    <SlideOutDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Business Task Evals"
      width="620px"
      modal={false}
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-border-subtle bg-background-secondary p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-text-primary">{name}</div>
              <div className="mt-1 text-xs text-text-muted">
                v{version}{statusText ? ` · ${statusText}` : ''}
              </div>
              {headerSummary && (
                <div className="mt-2 text-sm text-text-primary">{headerSummary}</div>
              )}
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              {suggestions.length > 0 && (
                <CopyTextButton text={copyAllText} label="Copy all" />
              )}
              <button
                type="button"
                className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs ${components.button.secondary}`}
                onClick={regenerate}
                disabled={loading || regenerating || !workflowId || isDemo}
                title="Regenerate eval suggestions"
              >
                <RefreshCw size={13} className={regenerating ? 'animate-spin' : ''} />
                Regenerate
              </button>
            </div>
          </div>
          {sharedRows.length > 0 && (
            <div className="mt-3">
              <TraceCasesSection
                rows={sharedRows}
                appId={appId}
                environment={environment}
                selectedRange={selectedRange}
              />
            </div>
          )}
        </div>

        {loading && (
          <div className="text-sm text-text-muted">Loading eval suggestions...</div>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded border px-3 py-2 text-sm" style={{
            color: palette.status.danger,
            borderColor: withOpacity(palette.status.danger, 35),
            backgroundColor: withOpacity(palette.status.danger, 8),
          }}>
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && suggestions.length === 0 && (
          <div className="text-sm text-text-muted">No eval suggestions available for this business task.</div>
        )}

        {!loading && suggestions.length > 0 && (
          <div className="space-y-4">
            {CATEGORY_ORDER.filter(category => groupedSuggestions[category]?.length).map(category => (
              <section key={category} className="space-y-2">
                <div className="text-[11px] font-semibold uppercase text-text-muted">{category}</div>
                <div className="space-y-2">
                  {groupedSuggestions[category].map((suggestion) => {
                    const key = suggestionKey(suggestion);
                    return (
                      <SuggestionCard
                        key={key}
                        suggestion={suggestion}
                        expanded={Boolean(expandedCards[key])}
                        onToggle={() => toggleCard(key)}
                        appId={appId}
                        environment={environment}
                        selectedRange={selectedRange}
                        context={drawerContext}
                        hideTraceCases={sharedRows.length > 0}
                      />
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </SlideOutDrawer>
  );
}
